import { For, createMemo } from 'solid-js'
import { isHabitPausedDuring } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import {
   getHabitDayKey,
   getHabitNextDueAt,
   getLocalDayStart,
   getLocalDayStartAfterDays,
   habits,
   type Habit,
} from '../../state/habitsStore'
import { createLiveClock } from '../../utils/liveClock'

const DAYS_SHOWN = 30
const HABIT_HISTORY_REFRESH_MS = 60 * 1000

type HabitDayState = 'empty' | 'partial' | 'complete'

/**
 * Returns all known completion timestamps for a habit, including
 * `lastCompletedAt` when it is missing from the (possibly truncated)
 * completion history.
 *
 * @param habit - The habit.
 * @returns The completion timestamps.
 */
function getCompletionTimestamps(habit: Habit) {
   if (
      habit.lastCompletedAt === null ||
      habit.completionHistory.includes(habit.lastCompletedAt)
   ) {
      return habit.completionHistory
   }
   return [...habit.completionHistory, habit.lastCompletedAt]
}

/**
 * Returns whether a habit was completed on a given local day.
 *
 * @param habit - The habit.
 * @param dayKey - The local day key.
 * @returns True when completed that day.
 */
function hasCompletionOnDay(habit: Habit, dayKey: string) {
   return getCompletionTimestamps(habit).some(
      timestamp => getHabitDayKey(timestamp) === dayKey,
   )
}

/**
 * Returns the latest completion timestamp at or before a time.
 *
 * @param habit - The habit.
 * @param timestamp - The upper-bound timestamp.
 * @returns The completion timestamp, or null.
 */
function getLatestCompletionBefore(habit: Habit, timestamp: number) {
   let latest: number | null = null
   for (const completion of getCompletionTimestamps(habit)) {
      if (completion < timestamp && (latest === null || completion > latest)) {
         latest = completion
      }
   }
   return latest
}

/**
 * Returns whether a recurring habit was either not yet due or completed on a
 * given local day.
 *
 * @param habit - The recurring habit.
 * @param dayStart - Start of the local day.
 * @param dayEnd - Start of the following local day.
 * @param effectiveDayEnd - `dayEnd`, clamped to "now" for the current day.
 * @returns True when the habit did not need attention that day.
 */
function isRecurringHabitFulfilledOnDay(
   habit: Habit,
   dayStart: number,
   dayEnd: number,
   effectiveDayEnd: number,
) {
   const latestCompletionBeforeDayStart = getLatestCompletionBefore(habit, dayStart)
   const nextDueAt =
      latestCompletionBeforeDayStart === null ?
         habit.createdAt
      :  getLocalDayStartAfterDays(latestCompletionBeforeDayStart, habit.scheduleDays)
   const isToday = effectiveDayEnd < dayEnd
   const isNotDueYet = isToday ? nextDueAt > effectiveDayEnd : nextDueAt >= dayEnd
   return isNotDueYet || hasCompletionOnDay(habit, getHabitDayKey(dayStart))
}

/**
 * Returns whether a habit counts toward a day's task total.
 *
 * Recurring habits count from the day they were created. One-off habits count
 * from the day they are due; completed ones are removed from the list, so any
 * one-off still present is an open task on every day since it became due.
 * Neither counts on a day it spent (even partly) paused.
 *
 * @param habit - The habit.
 * @param dayStart - Start of the local day.
 * @param dayEnd - Start of the following local day.
 * @param effectiveDayEnd - `dayEnd`, clamped to "now" for the current day.
 * @returns True when the habit is active that day.
 */
function isHabitActiveOnDay(
   habit: Habit,
   dayStart: number,
   dayEnd: number,
   effectiveDayEnd: number,
) {
   if (isHabitPausedDuring(habit, dayStart, effectiveDayEnd)) {
      return false
   }
   if (habit.kind === 'recurring') {
      return habit.createdAt <= effectiveDayEnd
   }
   // A one-off due date is a local-midnight timestamp, so a task due tomorrow
   // sits exactly on today's `dayEnd` and must not count for today.
   const dueAt = getHabitNextDueAt(habit)
   return dueAt < dayEnd && dueAt <= effectiveDayEnd
}

/**
 * Classifies a day as all/some/none complete from counts.
 *
 * @param totalCount - Total habits due that day.
 * @param fulfilledCount - Completed count.
 * @returns The day state.
 */
function getHabitDayState(totalCount: number, fulfilledCount: number): HabitDayState {
   if (totalCount === 0) {
      return 'empty'
   }
   if (fulfilledCount === totalCount) {
      return 'complete'
   }
   return fulfilledCount > 0 ? 'partial' : 'empty'
}

/**
 * Returns the most recent change time across habits (for cache keys).
 *
 * @param currentHabits - The habits.
 * @returns The latest change timestamp.
 */
function getLatestHabitChangeTime(currentHabits: Habit[]) {
   return currentHabits.reduce((latest, habit) => {
      const latestCompletion = Math.max(0, ...habit.completionHistory)
      return Math.max(
         latest,
         habit.createdAt,
         habit.updatedAt,
         habit.lastCompletedAt ?? 0,
         latestCompletion,
      )
   }, 0)
}

/**
 * Renders the colour legend under the habit history grid.
 *
 * @returns The legend element.
 */
const HabitTimeBlockLegend = () => {
   const t = useI18n()
   const entries: Array<{ state: HabitDayState; label: string }> = [
      { state: 'complete', label: t('All done') },
      { state: 'partial', label: t('Some done') },
      { state: 'empty', label: t('Missed or pending') },
   ]
   return (
      <div class="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-emerald-100/70">
         <For each={entries}>
            {entry => (
               <span class="flex items-center gap-1">
                  <span class="habit-time-block-legend" data-state={entry.state} />
                  {entry.label}
               </span>
            )}
         </For>
      </div>
   )
}

/**
 * Renders the habit completion history as a grid of day blocks.
 *
 * @returns The time-blocks element.
 */
const HabitTimeBlocks = () => {
   const t = useI18n()
   const [currentTime] = createLiveClock({
      intervalMs: HABIT_HISTORY_REFRESH_MS,
      refreshAtMidnight: true,
   })

   const dayBlocks = createMemo(() => {
      const currentHabits = habits()
      const referenceNow = Math.max(
         currentTime(),
         getLatestHabitChangeTime(currentHabits),
      )
      const todayStart = getLocalDayStart(referenceNow)

      return Array.from({ length: DAYS_SHOWN }, (_, index) => {
         const dayStart = getLocalDayStartAfterDays(todayStart, index - DAYS_SHOWN + 1)
         const dayEnd = getLocalDayStartAfterDays(dayStart, 1)
         const effectiveDayEnd = Math.min(dayEnd, referenceNow)
         const dayKey = getHabitDayKey(dayStart)
         const activeHabits = currentHabits.filter(habit =>
            isHabitActiveOnDay(habit, dayStart, dayEnd, effectiveDayEnd),
         )
         const fulfilledCount = activeHabits.filter(
            habit =>
               habit.kind === 'recurring' &&
               isRecurringHabitFulfilledOnDay(habit, dayStart, dayEnd, effectiveDayEnd),
         ).length
         const state = getHabitDayState(activeHabits.length, fulfilledCount)

         return {
            dayKey,
            label: new Date(dayStart).toLocaleDateString(undefined, {
               month: 'short',
               day: 'numeric',
            }),
            state,
            completedCount: fulfilledCount,
            totalCount: activeHabits.length,
         }
      })
   })

   const completeDayCount = createMemo(
      () => dayBlocks().filter(block => block.state === 'complete').length,
   )

   return (
      <section class="mt-5">
         <div class="mb-2 flex items-end justify-between gap-3">
            <div>
               <h3 class="text-sm font-semibold text-ink">{t('Habit history')}</h3>
               <p class="text-xs text-emerald-100/65">{t('Past 30 days')}</p>
            </div>
            <div class="text-right text-[11px] text-emerald-100/70">
               {t('{count} complete days', { count: completeDayCount() })}
            </div>
         </div>
         <div
            class="history-time-block-grid habit-history-time-block-grid"
            role="grid"
            aria-label={t('Habit completion history')}
         >
            <For each={dayBlocks()}>
               {block => (
                  <span
                     class="habit-time-block-cell"
                     data-state={block.state}
                     title={`${block.label}: ${block.completedCount}/${block.totalCount}`}
                     aria-label={`${block.label}: ${block.completedCount}/${block.totalCount}`}
                  />
               )}
            </For>
         </div>
         <HabitTimeBlockLegend />
      </section>
   )
}

export default HabitTimeBlocks
